<template>
  <div class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md">
    <div class="trezo-card-content relative">
        <span class="block">
            {{ directT('dashboard.crm.revenueGrowth') }}
        </span>
        <h5 class="mb-0 mt-[3px] text-[20px]" v-if="loading">
            {{ directT('dashboard.crm.loading') }}
        </h5>
        <h5 class="mb-0 mt-[3px] text-[20px]" v-else-if="!hasPreviousPeriodData">
            {{ directT('dashboard.noPreviousData') }}
        </h5>
        <h5 class="mb-0 mt-[3px] text-[20px]" v-else :class="{
            'text-success-600': revenueDifference > 0,
            'text-danger-600': revenueDifference < 0,
            'text-gray-600': revenueDifference === 0
        }">
            {{ revenueDifference > 0 ? '+' : '' }}{{ formatCurrency(Math.abs(revenueDifference), currencyCode) }}
        </h5>
        <div class="absolute -top-[28px] ltr:-right-[9px] rtl:-left-[9px] max-w-[120px]">
          <apexchart
          v-if="isClient && chartData.length > 0"
          type="bar"
          height="95"
          :options="revenueGrowth"
          :series="revenue"
        ></apexchart>
        </div>
        <div class="mt-[25px] md:mt-[34px] flex items-center justify-between">
            <span 
                v-if="!hasPreviousPeriodData" 
                class="inline-block text-sm text-gray-500 py-[1px] px-[8.3px] rounded-xl"
            >
                {{ directT('dashboard.noPreviousData') }}
            </span>
            <span 
                v-else
                class="inline-block text-sm py-[1px] px-[8.3px] border rounded-xl"
                :class="{
                  'text-success-700 border-success-300 bg-success-100': percentageChange > 0,
                  'text-danger-700 border-danger-300 bg-danger-100': percentageChange < 0,
                  'text-gray-700 border-gray-300 bg-gray-100': percentageChange === 0
                }"
            >
                {{ percentageChange > 0 ? '+' : '' }}{{ percentageChange }}%
            </span>
            <span class="block text-sm">
                {{ directT('dashboard.last30Days') }}
            </span>
        </div>
    </div>
</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, onUnmounted } from "vue";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentOrgId } from "@/utils/getCurrentOrgId";
import { formatCurrency, getUserCurrency } from "@/utils/currency";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for nested translation objects
type TranslationObject = { [key: string]: string | TranslationObject };

export default defineComponent({
  name: "RevenueGrowthChart",
  props: {
    currentUserId: {
      type: String,
      default: null
    },
    userPermissions: {
      type: Object,
      default: () => ({
        is_owner: false,
        edit_all_leads: false,
        view_all_leads: false,
        can_invite_users: false
      })
    }
  },
  setup(props) {
    const isClient = ref(false);
    const loading = ref(true);
    const totalRevenue = ref(0);
    const previousRevenue = ref(0);
    const percentageChange = ref(0);
    const hasPreviousPeriodData = ref(false);
    const organizationId = ref<string | null>(null);
    const chartData = ref<{current: number, previous: number}[]>([]);
    const refreshTimer = ref<number | null>(null);
    const currencyCode = ref('ILS'); // ברירת מחדל
    
    // i18n setup
    const { locale } = useI18n();
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };
    
    // Compute the difference between current and previous period
    const revenueDifference = computed(() => {
      return totalRevenue.value - previousRevenue.value;
    });
    
    const revenue = computed(() => [
      {
        name: directT('dashboard.currentPeriod'),
        data: [totalRevenue.value],
      },
      {
        name: directT('dashboard.previousPeriod'),
        data: [previousRevenue.value],
      }
    ]);
    
    const revenueGrowth = ref({
      chart: {
        type: "bar",
        height: 95,
        stacked: false,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      colors: ["#3584FC", "#94A3B8"],
      dataLabels: {
        enabled: false,
      },
      grid: {
        show: false,
        borderColor: "#ECEEF2",
      },
      stroke: {
        width: 0,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '45%',
        },
      },
      xaxis: {
        categories: ["Revenue"],
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        labels: {
          show: false,
        },
      },
      yaxis: {
        show: false,
        labels: {
          formatter: (val: string) => {
            return formatCurrency(Number(val), currencyCode.value);
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function(val: number) {
            return formatCurrency(val, currencyCode.value);
          }
        }
      }
    });

    // Get date from X days ago in ISO format
    const getDateXDaysAgo = (days: number) => {
      const date = new Date();
      date.setDate(date.getDate() - days);
      return date.toISOString();
    };
    
    // Format amount to display with comma separators
    const formatAmount = (amount: number) => {
      return Math.abs(amount).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    };
    
    // Get the organization's currency
    const getCurrency = async () => {
      try {
        currencyCode.value = await getUserCurrency();
      } catch (error) {
        console.error('Error fetching currency:', error);
      }
    };
    
    // Fetch revenue data
    const fetchRevenueData = async () => {
      try {
        loading.value = true;
        organizationId.value = await getCurrentOrgId();
        
        if (!organizationId.value) {
          console.error("User is not associated with an organization");
          loading.value = false;
          return;
        }
        
        const thirtyDaysAgo = getDateXDaysAgo(30);
        const sixtyDaysAgo = getDateXDaysAgo(60);
        
        // Build query based on user permissions - use dealtime for current period
        let query = supabase
          .from('leads')
          .select('dealamount, dealtime')
          .eq('org_id', organizationId.value)
          .not('dealtime', 'is', null)
          .not('dealamount', 'is', null)
          .gte('dealtime', thirtyDaysAgo);
          
        // If user can't view all leads, only show assigned leads
        if (props.currentUserId && !props.userPermissions.is_owner && !props.userPermissions.view_all_leads) {
          query = query.eq('assigned_to', props.currentUserId);
        }
        
        // Execute query
        const { data, error } = await query;
        
        if (error) throw error;
        
        // Calculate total revenue for current period
        let currentTotal = 0;
        if (data && data.length > 0) {
          currentTotal = data.reduce((sum, deal) => {
            const amount = typeof deal.dealamount === 'number' ? deal.dealamount : 0;
            return sum + amount;
          }, 0);
        }
        
        console.log(`Revenue Growth: Current period revenue (by dealtime) - $${currentTotal}`);
        totalRevenue.value = currentTotal;
        
        // Fetch deals closed in the previous 30 days (30-60 days ago)
        let previousQuery = supabase
          .from('leads')
          .select('dealamount, dealtime')
          .eq('org_id', organizationId.value)
          .not('dealtime', 'is', null)
          .not('dealamount', 'is', null)
          .gte('dealtime', sixtyDaysAgo)
          .lt('dealtime', thirtyDaysAgo);
          
        // If user can't view all leads, only show assigned leads
        if (props.currentUserId && !props.userPermissions.is_owner && !props.userPermissions.view_all_leads) {
          previousQuery = previousQuery.eq('assigned_to', props.currentUserId);
        }
        
        // Execute query for previous period
        const { data: previousDeals, error: previousError } = await previousQuery;
        
        if (previousError) throw previousError;
        
        // Calculate total revenue for previous period
        let previousTotal = 0;
        if (previousDeals && previousDeals.length > 0) {
          previousTotal = previousDeals.reduce((sum, deal) => {
            const amount = typeof deal.dealamount === 'number' ? deal.dealamount : 0;
            return sum + amount;
          }, 0);
        }
        
        console.log(`Revenue Growth: Previous period revenue - $${previousTotal}`);
        previousRevenue.value = previousTotal;
        
        // Check if there was revenue in the previous period
        hasPreviousPeriodData.value = previousTotal > 0;
        
        // Calculate percentage change
        if (hasPreviousPeriodData.value) {
          if (previousTotal === 0) {
            percentageChange.value = currentTotal > 0 ? 100 : 0;
          } else {
            const change = ((currentTotal - previousTotal) / previousTotal) * 100;
            percentageChange.value = Math.round(change);
          }
          console.log(`Revenue Growth: Percentage change - ${percentageChange.value}%`);
        } else {
          percentageChange.value = 0;
        }
        
        chartData.value = [{current: currentTotal, previous: previousTotal}];
      } catch (error) {
        console.error('Error fetching revenue data:', error);
      } finally {
        loading.value = false;
      }
    };
    
    // Start a timer to refresh data periodically
    const startRefreshTimer = () => {
      // Refresh every 30 seconds
      refreshTimer.value = window.setInterval(() => {
        console.log('Refreshing Revenue Growth data...');
        fetchRevenueData();
      }, 30000); // 30 seconds
    };
    
    onMounted(async () => {
      isClient.value = true;
      await getCurrency();
      fetchRevenueData();
      startRefreshTimer();
    });
    
    onUnmounted(() => {
      // Clear the refresh timer when component is unmounted
      if (refreshTimer.value) {
        clearInterval(refreshTimer.value);
      }
    });

    return {
      isClient,
      loading,
      hasPreviousPeriodData,
      totalRevenue,
      previousRevenue,
      revenueDifference,
      percentageChange,
      revenue,
      revenueGrowth,
      chartData,
      formatAmount,
      formatCurrency,
      currencyCode,
      directT,
    };
  },
});
</script>
